import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { IoMdArrowBack } from "react-icons/io";
import { HiOutlinePlus } from "react-icons/hi";
import {
  hrRecordsApi,
  hrRecordsFormDataHelpers,
} from "../../Services/hrRecordsApi";
import {
  ATTENDANCE_STATUS_OPTIONS,
  RESULT_STATUS_OPTIONS,
  REPEAT_UNIT_OPTIONS,
  SESSION_STATUS_OPTIONS,
  HR_IMAGE_UPLOAD_ACCEPT,
  HR_IMAGE_UPLOAD_EXTENSIONS,
  TRAINING_MODE_OPTIONS,
  buildEmployeeSelectOptions,
  buildSimpleOptions,
  downloadBlobResponse,
  getApiErrorMessage,
  mergeEmployeeSelectOptions,
  normaliseReminderDays,
  openBlobResponse,
} from "../hrRecordsHelpers";
import HrUploadControl from "../HrUploadControl";
import "../../forms/UserForm.css";
import "../hrRecords.css";

const emptyParticipant = () => ({
  participantId: "",
  actualManPowerId: "",
  attendanceStatus: "present",
  resultStatus: "pass",
  score: "",
  certificateNumber: "",
  issueDate: null,
  expiryDate: null,
  nextDueDate: null,
  remarks: "",
  newCertificateFiles: [],
  certificateCount: 0,
});

const emptySessionFiles = () => ({
  attendanceSheets: [],
  photographs: [],
  materials: [],
  attachments: [],
});

const toApiDate = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseApiDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const normaliseFileList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (value.file_id || value.filename || value.name) return [value];
  return Object.values(value).flatMap((item) => normaliseFileList(item));
};

const normaliseSessionAttachments = (attachments = {}) => {
  const grouped = {
    attendance_sheet: [],
    photograph: [],
    material: [],
    other: [],
  };

  if (Array.isArray(attachments)) {
    attachments.forEach((file) => {
      const docType = (
        file.doc_type ||
        file.file_type ||
        file.attachment_type ||
        "other"
      ).toLowerCase();

      if (docType.includes("attendance")) {
        grouped.attendance_sheet.push(file);
      } else if (docType.includes("photo") || docType.includes("photograph")) {
        grouped.photograph.push(file);
      } else if (docType.includes("material")) {
        grouped.material.push(file);
      } else {
        grouped.other.push(file);
      }
    });
    return grouped;
  }

  grouped.attendance_sheet = normaliseFileList(
    attachments.attendance_sheet ||
      attachments.attendance_sheets ||
      attachments.attendanceSheets,
  );
  grouped.photograph = normaliseFileList(
    attachments.photograph || attachments.photographs,
  );
  grouped.material = normaliseFileList(
    attachments.material || attachments.materials,
  );
  grouped.other = normaliseFileList(
    attachments.other ||
      attachments.attachment ||
      attachments.attachments ||
      attachments.other_attachments,
  );

  return grouped;
};

function TrainingSessionFormPage({ isSidebarExpanded, mode = "create" }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = mode === "edit";
  const sessionId = id;

  const [formData, setFormData] = useState({
    trainingMasterId: "",
    trainingCategory: "",
    standardBody: "",
    standardRefNo: "",
    standardSectionNo: "",
    standardDescription: "",
    graceDays: "",
    isMandatory: false,
    startDate: null,
    endDate: null,
    sessionStatus: "completed",
    trainingMode: "",
    trainerName: "",
    trainerOrganization: "",
    venue: "",
    durationHours: "",
    repeatEvery: "",
    repeatUnit: "",
    reminderDaysBefore: "",
    remarks: "",
  });
  const [masters, setMasters] = useState([]);
  const [standards, setStandards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [participants, setParticipants] = useState([emptyParticipant()]);
  const [existingSessionAttachments, setExistingSessionAttachments] = useState({
    attendance_sheet: [],
    photograph: [],
    material: [],
    other: [],
  });
  const [existingParticipantAttachments, setExistingParticipantAttachments] = useState({});
  const [sessionFiles, setSessionFiles] = useState(emptySessionFiles());
  const [removedParticipantIds, setRemovedParticipantIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEdit);
  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const fetchLookups = async () => {
    const [
      masterResponse,
      employeeResponse,
      standardsResponse,
      categoriesResponse,
    ] = await Promise.all([
      hrRecordsApi.listTrainingMasters({ page: 1, per_page: 200 }),
      hrRecordsApi.fetchEmployeesDropdown(),
      hrRecordsApi.listTrainingStandards(),
      hrRecordsApi.getTrainingCategories(),
    ]);

    const masterOptions = buildSimpleOptions(
      masterResponse.items || [],
      "id",
      "training_name",
    );

    setMasters(masterOptions);
    setEmployees(buildEmployeeSelectOptions(employeeResponse.data || []));
    setStandards(
      buildSimpleOptions(standardsResponse || [], "standard_body", "display_name"),
    );
    setCategories(
      buildSimpleOptions(categoriesResponse || [], "category_name", "category_name"),
    );

    return masterOptions;
  };

  const buildMasterFields = (master = {}) => ({
    trainingCategory: master.training_category || "",
    standardBody: master.standard_body || "",
    standardRefNo: master.standard_ref_no || "",
    standardSectionNo: master.standard_section_no || "",
    standardDescription: master.standard_description || "",
    graceDays: master.grace_days || "",
    isMandatory: Boolean(master.is_mandatory),
  });

  const buildMasterDefaults = (master = {}) => ({
    ...buildMasterFields(master),
    durationHours: master.default_duration_hours || "",
    repeatEvery: master.repeat_every || "",
    repeatUnit: master.repeat_unit || "",
    reminderDaysBefore: normaliseReminderDays(master.reminder_days_before),
  });

  const fetchSession = async (masterOptions = masters) => {
    if (!isEdit || !sessionId) return;

    const response = await hrRecordsApi.getTrainingSession(sessionId);
    const selectedMaster =
      masterOptions.find(
        (master) => String(master.value) === String(response.training_master_id),
      ) || {};
    const masterFields = buildMasterFields({ ...selectedMaster, ...response });

    setFormData({
      trainingMasterId: response.training_master_id || "",
      ...masterFields,
      startDate: parseApiDate(response.start_date),
      endDate: parseApiDate(response.end_date),
      sessionStatus: response.session_status || "completed",
      trainingMode: response.training_mode || "",
      trainerName: response.trainer_name || "",
      trainerOrganization: response.trainer_organization || "",
      venue: response.venue || "",
      durationHours:
        response.duration_hours || selectedMaster.default_duration_hours || "",
      repeatEvery:
        response.repeat_every ||
        response.effective_repeat_every ||
        selectedMaster.repeat_every ||
        "",
      repeatUnit:
        response.repeat_unit ||
        response.effective_repeat_unit ||
        selectedMaster.repeat_unit ||
        "",
      reminderDaysBefore: normaliseReminderDays(
        response.reminder_days_before ||
          response.effective_reminder_days ||
          selectedMaster.reminder_days_before,
      ),
      remarks: response.remarks || "",
    });

    const nextParticipants = (response.participants || []).length
      ? response.participants.map((participant) => ({
            participantId: participant.participant_id,
            actualManPowerId: String(participant.actual_man_power_id || ""),
            attendanceStatus: participant.attendance_status || "present",
            resultStatus: participant.result_status || "pass",
            score: participant.score ?? "",
            certificateNumber: participant.certificate_number || "",
            issueDate: parseApiDate(participant.issue_date),
            expiryDate: parseApiDate(participant.expiry_date),
            nextDueDate: parseApiDate(participant.next_due_date),
            remarks: participant.remarks || "",
            newCertificateFiles: [],
            certificateCount: participant.certificate_count || 0,
          }))
      : [emptyParticipant()];

    setParticipants(nextParticipants);
    setEmployees((currentEmployees) =>
      mergeEmployeeSelectOptions(currentEmployees, response.participants || []),
    );
    setExistingSessionAttachments(normaliseSessionAttachments(response.attachments));

    const participantsWithCertificates = nextParticipants.filter(
      (participant) => participant.participantId,
    );
    if (participantsWithCertificates.length) {
      const attachmentEntries = await Promise.all(
        participantsWithCertificates.map(async (participant) => {
          try {
            const filesResponse = await hrRecordsApi.listParticipantAttachments(
              participant.participantId,
            );
            return [
              participant.participantId,
              normaliseFileList(filesResponse.attachments || filesResponse),
            ];
          } catch (error) {
            console.error("Failed to load participant certificates:", error);
            return [participant.participantId, []];
          }
        }),
      );
      setExistingParticipantAttachments(Object.fromEntries(attachmentEntries));
    } else {
      setExistingParticipantAttachments({});
    }
  };

  useEffect(() => {
    const run = async () => {
      try {
        setPageLoading(true);
        const masterOptions = await fetchLookups();
        await fetchSession(masterOptions);
      } catch (error) {
        console.error("Failed to load session form:", error);
        showSnackbar("Failed to load training session data", "error");
      } finally {
        setPageLoading(false);
      }
    };

    run();
  }, [sessionId, isEdit]);

  const selectedStandard = useMemo(
    () =>
      standards.find(
        (standard) => String(standard.value) === String(formData.standardBody),
      ) || null,
    [standards, formData.standardBody],
  );
  const standardRefNoLabel = selectedStandard?.ref_no_label || "Standard Reference No.";
  const standardSectionNoLabel =
    selectedStandard?.section_label || "Standard Section No.";

  const selectedEmployeeValues = useMemo(
    () => participants.map((participant) => participant.actualManPowerId).filter(Boolean),
    [participants],
  );

  const getEmployeeValue = (value) =>
    employees.find((employee) => String(employee.value) === String(value)) || null;

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleStandardBodyChange = (option) => {
    handleFormChange("standardBody", option?.value || "");
  };

  const handleTrainingMasterChange = (option) => {
    setFormData((prev) => ({
      ...prev,
      trainingMasterId: option?.value || "",
      ...(option ? buildMasterDefaults(option) : buildMasterDefaults()),
    }));
    setErrors((prev) => ({ ...prev, trainingMasterId: "" }));
  };

  const handleParticipantChange = (index, field, value) => {
    setParticipants((prev) =>
      prev.map((participant, rowIndex) =>
        rowIndex === index ? { ...participant, [field]: value } : participant,
      ),
    );
  };

  const addParticipant = () => {
    setParticipants((prev) => [...prev, emptyParticipant()]);
  };

  const removeParticipant = (index) => {
    setParticipants((prev) => {
      const removed = prev[index];
      if (removed?.participantId) {
        setRemovedParticipantIds((current) => [...current, removed.participantId]);
      }
      const next = prev.filter((_, rowIndex) => rowIndex !== index);
      return next.length ? next : [emptyParticipant()];
    });
  };

  const addFilesToBucket = (bucket, files) => {
    const incoming = Array.from(files || []);
    setSessionFiles((prev) => ({
      ...prev,
      [bucket]: [...prev[bucket], ...incoming],
    }));
  };

  const removeNewSessionFile = (bucket, index) => {
    setSessionFiles((prev) => ({
      ...prev,
      [bucket]: prev[bucket].filter((_, fileIndex) => fileIndex !== index),
    }));
  };

  const removeExistingSessionFile = async (fileId) => {
    try {
      await hrRecordsApi.deleteTrainingSessionAttachment(fileId);
      setExistingSessionAttachments((prev) =>
        Object.fromEntries(
          Object.entries(prev).map(([key, files]) => [
            key,
            files.filter((file) => (file.file_id || file.id) !== fileId),
          ]),
        ),
      );
      showSnackbar("Attachment deleted successfully");
    } catch (error) {
      console.error("Failed to delete attachment:", error);
      showSnackbar(
        getApiErrorMessage(error, "Failed to delete attachment"),
        "error",
      );
    }
  };

  const removeExistingParticipantCertificate = async (participantId, fileId) => {
    try {
      await hrRecordsApi.deleteParticipantAttachment(fileId);
      setExistingParticipantAttachments((prev) => ({
        ...prev,
        [participantId]: (prev[participantId] || []).filter(
          (file) => (file.file_id || file.id) !== fileId,
        ),
      }));
      setParticipants((prev) =>
        prev.map((participant) =>
          participant.participantId === participantId
            ? {
                ...participant,
                certificateCount: Math.max(
                  0,
                  Number(participant.certificateCount || 0) - 1,
                ),
              }
            : participant,
        ),
      );
      showSnackbar("Certificate attachment deleted successfully");
    } catch (error) {
      console.error("Failed to delete certificate attachment:", error);
      showSnackbar(
        getApiErrorMessage(error, "Failed to delete certificate attachment"),
        "error",
      );
    }
  };

  const handleFileAction = async (request, filename, action = "download") => {
    try {
      const response = await request;
      if (action === "view") {
        openBlobResponse(response);
      } else {
        downloadBlobResponse(response, filename);
      }
    } catch (error) {
      console.error("File action failed:", error);
      showSnackbar(getApiErrorMessage(error, "File action failed"), "error");
    }
  };

  const addParticipantCertificates = (index, files) => {
    const incoming = Array.from(files || []);
    handleParticipantChange(index, "newCertificateFiles", [
      ...(participants[index]?.newCertificateFiles || []),
      ...incoming,
    ]);
  };

  const removeParticipantCertificate = (index, fileIndex) => {
    const next = [...(participants[index]?.newCertificateFiles || [])];
    next.splice(fileIndex, 1);
    handleParticipantChange(index, "newCertificateFiles", next);
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.trainingMasterId) {
      nextErrors.trainingMasterId = "Training master is required";
    }
    if (!formData.startDate) {
      nextErrors.startDate = "Start date is required";
    }
    const validParticipants = participants.filter(
      (participant) => participant.actualManPowerId,
    );
    if (!validParticipants.length) {
      nextErrors.participants = "At least one participant is required";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildSessionPayload = () => ({
    training_master_id: formData.trainingMasterId,
    training_category: formData.trainingCategory || undefined,
    standard_body: formData.standardBody || undefined,
    standard_ref_no: formData.standardRefNo || undefined,
    standard_section_no: formData.standardSectionNo || undefined,
    standard_description: formData.standardDescription || undefined,
    grace_days: formData.graceDays || undefined,
    is_mandatory: formData.isMandatory,
    start_date: toApiDate(formData.startDate),
    end_date: toApiDate(formData.endDate || formData.startDate),
    session_status: formData.sessionStatus || undefined,
    training_mode: formData.trainingMode || undefined,
    trainer_name: formData.trainerName || undefined,
    trainer_organization: formData.trainerOrganization || undefined,
    venue: formData.venue || undefined,
    duration_hours: formData.durationHours || undefined,
    repeat_every: formData.repeatEvery || undefined,
    repeat_unit: formData.repeatUnit || undefined,
    reminder_days_before: formData.reminderDaysBefore || undefined,
    remarks: formData.remarks || undefined,
  });

  const buildParticipantPayload = (participant) => ({
    actual_man_power_id: participant.actualManPowerId,
    attendance_status: participant.attendanceStatus || undefined,
    result_status: participant.resultStatus || undefined,
    score: participant.score || undefined,
    certificate_number: participant.certificateNumber || undefined,
    issue_date: toApiDate(participant.issueDate) || undefined,
    expiry_date: toApiDate(participant.expiryDate) || undefined,
    next_due_date: toApiDate(participant.nextDueDate) || undefined,
    remarks: participant.remarks || undefined,
  });

  const uploadSessionFiles = async (currentSessionId) => {
    const hasFiles = Object.values(sessionFiles).some((files) => files.length);
    if (!hasFiles) return;

    await hrRecordsApi.uploadTrainingSessionAttachments(currentSessionId, sessionFiles);
  };

  const uploadParticipantFiles = async (participantMap) => {
    const uploads = participants
      .filter(
        (participant) =>
          participant.actualManPowerId &&
          participant.newCertificateFiles.length &&
          participantMap[participant.actualManPowerId],
      )
      .map((participant) =>
        hrRecordsApi.uploadParticipantCertificate(
          participantMap[participant.actualManPowerId],
          participant.newCertificateFiles,
        ),
      );

    if (uploads.length) {
      await Promise.all(uploads);
    }
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      setLoading(true);
      const validParticipants = participants.filter(
        (participant) => participant.actualManPowerId,
      );

      if (!isEdit) {
        await hrRecordsApi.createTrainingSession(
          hrRecordsFormDataHelpers.buildTrainingSessionCreateFormData({
            session: buildSessionPayload(),
            participants: validParticipants.map(buildParticipantPayload),
            sessionFiles,
            participantFilesByEmployee: Object.fromEntries(
              validParticipants
                .filter(
                  (participant) =>
                    participant.actualManPowerId &&
                    participant.newCertificateFiles.length,
                )
                .map((participant) => [
                  participant.actualManPowerId,
                  participant.newCertificateFiles,
                ]),
            ),
          }),
        );

        showSnackbar("Training session created successfully");
        window.setTimeout(() => {
          navigate("/training/training-sessions");
        }, 900);
        return;
      }

      const sessionResponse = await hrRecordsApi.updateTrainingSession(
        sessionId,
        buildSessionPayload(),
      );

      const currentSessionId = sessionResponse.id || sessionId;

      if (removedParticipantIds.length) {
        await Promise.all(
          removedParticipantIds.map((participantId) =>
            hrRecordsApi.removeTrainingParticipant(participantId),
          ),
        );
      }

      const existingParticipants = validParticipants.filter(
        (participant) => participant.participantId,
      );
      const newParticipants = validParticipants.filter(
        (participant) => !participant.participantId,
      );

      if (existingParticipants.length) {
        await Promise.all(
          existingParticipants.map((participant) =>
            hrRecordsApi.updateTrainingParticipant(
              participant.participantId,
              buildParticipantPayload(participant),
            ),
          ),
        );
      }

      const participantMap = Object.fromEntries(
        existingParticipants.map((participant) => [
          participant.actualManPowerId,
          participant.participantId,
        ]),
      );

      if (newParticipants.length) {
        const saveResponse = await hrRecordsApi.saveTrainingParticipants(
          currentSessionId,
          {
            participants: newParticipants.map(buildParticipantPayload),
          },
        );
        (saveResponse.participants || []).forEach((participant) => {
          participantMap[participant.actual_man_power_id] = participant.participant_id;
        });
      }

      await uploadSessionFiles(currentSessionId);
      await uploadParticipantFiles(participantMap);

      showSnackbar(
        `Training session ${isEdit ? "updated" : "created"} successfully`,
      );
      window.setTimeout(() => {
        navigate("/training/training-sessions");
      }, 900);
    } catch (error) {
      console.error("Failed to save training session:", error);
      showSnackbar(
        getApiErrorMessage(error, "Failed to save training session"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content">
          <div className="data-form-header">
            <div className="data-form-header-content">
              <div className="data-form-heading-content-left-side">
                <Tooltip title="Back" arrow>
                  <IoMdArrowBack
                    className="back-company"
                    onClick={() => navigate("/training/training-sessions")}
                  />
                </Tooltip>
                {isEdit ? "Edit Training Session" : "New Training Session"}
              </div>
            </div>
          </div>
          <div className="header-separator"></div>

          <div className="data-form-body">
            {pageLoading ? (
              <div className="loader-container">
                <div className="loader"></div>
                <div>Loading...</div>
              </div>
            ) : (
              <form className="form-detail">
                <div className="new-data-form hr-records-form-grid-two">
                  <div className="form-group-select">
                    <label htmlFor="training_master_id" className="hr_training_labels">
                      Training from catalogue <span className="text-danger">*</span>
                    </label>
                    <div className="input-group hr-records-form-control">
                      <Select
                        id="training_master_id"
                        className="hr-records-select"
                        options={masters}
                        value={masters.find(
                          (master) => master.value === formData.trainingMasterId,
                        )}
                        onChange={handleTrainingMasterChange}
                        placeholder="Select training master"
                        isSearchable
                      />
                    </div>
                    {errors.trainingMasterId && (
                      <div className="error-message">{errors.trainingMasterId}</div>
                    )}
                  </div>

                  <div className="form-group-select">
                    <label className="hr_training_labels">Training Category</label>
                    <div className="input-group hr-records-form-control">
                      <Select
                        className="hr-records-select"
                        options={categories}
                        value={categories.find(
                          (category) => category.value === formData.trainingCategory,
                        )}
                        onChange={(option) =>
                          handleFormChange("trainingCategory", option?.value || "")
                        }
                        isClearable
                        placeholder="Select category"
                      />
                    </div>
                  </div>

                  <div className="form-group-select">
                    <label className="hr_training_labels">Standard Body</label>
                    <div className="input-group hr-records-form-control">
                      <Select
                        className="hr-records-select"
                        options={standards}
                        value={selectedStandard}
                        onChange={handleStandardBodyChange}
                        placeholder="Select standard body"
                        isClearable
                        isSearchable
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="hr_training_labels">{standardRefNoLabel}</label>
                    <div className="input-group hr-records-form-control">
                      <input
                        type="text"
                        value={formData.standardRefNo}
                        onChange={(event) =>
                          handleFormChange("standardRefNo", event.target.value)
                        }
                        placeholder="Auto-filled from master"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="hr_training_labels">{standardSectionNoLabel}</label>
                    <div className="input-group hr-records-form-control">
                      <input
                        type="text"
                        value={formData.standardSectionNo}
                        onChange={(event) =>
                          handleFormChange("standardSectionNo", event.target.value)
                        }
                        placeholder="Auto-filled from master"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="hr_training_labels">Grace Days</label>
                    <div className="input-group hr-records-form-control">
                      <input
                        type="number"
                        value={formData.graceDays}
                        onChange={(event) =>
                          handleFormChange("graceDays", event.target.value)
                        }
                        placeholder="Auto-filled from master"
                      />
                    </div>
                  </div>

                  <div className="form-group hr-records-checkbox-row">
                    <label className="hr_training_labels">Mandatory Training</label>
                    <div className="input-group hr-records-checkbox-control">
                      <input
                        type="checkbox"
                        checked={formData.isMandatory}
                        onChange={(event) =>
                          handleFormChange("isMandatory", event.target.checked)
                        }
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="hr_training_labels">Standard Description</label>
                    <div className="input-group hr-records-form-control">
                      <textarea
                        rows={2}
                        value={formData.standardDescription}
                        onChange={(event) =>
                          handleFormChange(
                            "standardDescription",
                            event.target.value,
                          )
                        }
                        style={{ width: "100%", resize: "vertical" }}
                        placeholder="Auto-filled from master"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="start_date" className="hr_training_labels">
                      Start Date <span className="text-danger">*</span>
                    </label>
                    <div className="input-group hr-records-form-control">
                      <DatePicker
                        selected={formData.startDate}
                        onChange={(date) => handleFormChange("startDate", date)}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Select start date"
                        className="datesss"
                        wrapperClassName="hr-records-date-wrapper"
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={30}
                      />
                    </div>
                    {errors.startDate && (
                      <div className="error-message">{errors.startDate}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="end_date" className="hr_training_labels">
                      End Date
                    </label>
                    <div className="input-group hr-records-form-control">
                      <DatePicker
                        selected={formData.endDate}
                        onChange={(date) => handleFormChange("endDate", date)}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Select end date"
                        className="datesss"
                        wrapperClassName="hr-records-date-wrapper"
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={30}
                      />
                    </div>
                  </div>

                  <div className="form-group-select">
                    <label htmlFor="session_status" className="hr_training_labels">
                      Session Status
                    </label>
                    <div className="input-group hr-records-form-control">
                      <Select
                        id="session_status"
                        className="hr-records-select"
                        options={SESSION_STATUS_OPTIONS}
                        value={SESSION_STATUS_OPTIONS.find(
                          (option) => option.value === formData.sessionStatus,
                        )}
                        onChange={(option) =>
                          handleFormChange("sessionStatus", option?.value || "")
                        }
                        placeholder="Select session status"
                      />
                    </div>
                  </div>

                  <div className="form-group-select">
                    <label htmlFor="training_mode" className="hr_training_labels">
                      Training Mode
                    </label>
                    <div className="input-group hr-records-form-control">
                      <Select
                        id="training_mode"
                        className="hr-records-select"
                        options={TRAINING_MODE_OPTIONS}
                        value={TRAINING_MODE_OPTIONS.find(
                          (option) => option.value === formData.trainingMode,
                        )}
                        onChange={(option) =>
                          handleFormChange("trainingMode", option?.value || "")
                        }
                        placeholder="Select training mode"
                        isClearable
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="trainer_name" className="hr_training_labels">
                      Trainer Name
                    </label>
                    <div className="input-group hr-records-form-control">
                      <input
                        type="text"
                        name="trainer_name"
                        value={formData.trainerName}
                        onChange={(event) =>
                          handleFormChange("trainerName", event.target.value)
                        }
                        placeholder="Enter trainer name"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="trainer_organization" className="hr_training_labels">
                      Trainer Organization
                    </label>
                    <div className="input-group hr-records-form-control">
                      <input
                        type="text"
                        name="trainer_organization"
                        value={formData.trainerOrganization}
                        onChange={(event) =>
                          handleFormChange(
                            "trainerOrganization",
                            event.target.value,
                          )
                        }
                        placeholder="Enter trainer organization"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="venue" className="hr_training_labels">
                      Venue
                    </label>
                    <div className="input-group hr-records-form-control">
                      <input
                        type="text"
                        name="venue"
                        value={formData.venue}
                        onChange={(event) =>
                          handleFormChange("venue", event.target.value)
                        }
                        placeholder="Enter venue"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="duration_hours" className="hr_training_labels">
                      Duration Hours
                    </label>
                    <div className="input-group hr-records-form-control">
                      <input
                        type="number"
                        name="duration_hours"
                        value={formData.durationHours}
                        onChange={(event) =>
                          handleFormChange("durationHours", event.target.value)
                        }
                        placeholder="Enter duration hours"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="repeat_every" className="hr_training_labels">
                      Repeat Every
                    </label>
                    <div className="input-group hr-records-form-control">
                      <input
                        type="number"
                        name="repeat_every"
                        value={formData.repeatEvery}
                        onChange={(event) =>
                          handleFormChange("repeatEvery", event.target.value)
                        }
                        placeholder="e.g. 12"
                      />
                    </div>
                  </div>

                  <div className="form-group-select">
                    <label htmlFor="repeat_unit" className="hr_training_labels">
                      Repeat Unit
                    </label>
                    <div className="input-group hr-records-form-control">
                      <Select
                        id="repeat_unit"
                        className="hr-records-select"
                        options={REPEAT_UNIT_OPTIONS}
                        value={REPEAT_UNIT_OPTIONS.find(
                          (option) => option.value === formData.repeatUnit,
                        )}
                        onChange={(option) =>
                          handleFormChange("repeatUnit", option?.value || "")
                        }
                        placeholder="Select repeat unit"
                        isClearable
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="reminder_days_before" className="hr_training_labels">
                      Reminder Days Before
                    </label>
                    <div className="input-group hr-records-form-control">
                      <input
                        type="text"
                        name="reminder_days_before"
                        value={formData.reminderDaysBefore}
                        onChange={(event) =>
                          handleFormChange("reminderDaysBefore", event.target.value)
                        }
                        placeholder="e.g. 30,15,7"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="remarks" className="hr_training_labels">
                      Remarks
                    </label>
                    <div className="input-group hr-records-form-control">
                      <textarea
                        name="remarks"
                        rows={3}
                        value={formData.remarks}
                        onChange={(event) =>
                          handleFormChange("remarks", event.target.value)
                        }
                        style={{ width: "100%", resize: "vertical" }}
                        placeholder="Enter remarks"
                      />
                    </div>
                  </div>
                </div>

                <div className="hr-records-section">
                  <div className="hr-records-section-title">Participants</div>
                  {errors.participants && (
                    <div className="error-message">{errors.participants}</div>
                  )}
                  {participants.map((participant, index) => (
                    <div key={`${participant.participantId || "new"}-${index}`} className="hr-records-participant-card">
                      <div className="hr-records-participant-header">
                        <strong>Participant {index + 1}</strong>
                        <div className="hr-records-inline-actions">
                          {participant.certificateCount > 0 && (
                            <span style={{ color: "#68768a", fontSize: "12px" }}>
                              Existing certificates: {participant.certificateCount}
                            </span>
                          )}
                          <button
                            type="button"
                            className="data-form-cancel-button"
                            onClick={() => removeParticipant(index)}
                          >
                            Remove
                          </button>
                        </div>
                      </div>

                      <div className="hr-records-card-grid hr-records-card-grid-four">
                        <div className="hr-records-card-field">
                          <label>Employee</label>
                          <Select
                            className="hr-records-select"
                            options={employees.filter(
                              (employee) =>
                                !selectedEmployeeValues.includes(String(employee.value)) ||
                                String(employee.value) ===
                                  String(participant.actualManPowerId),
                            )}
                            value={getEmployeeValue(participant.actualManPowerId)}
                            onChange={(option) =>
                              handleParticipantChange(
                                index,
                                "actualManPowerId",
                                option?.value || "",
                              )
                            }
                            placeholder="Select employee"
                            isSearchable
                          />
                        </div>

                        <div className="hr-records-card-field">
                          <label>Attendance</label>
                          <Select
                            className="hr-records-select"
                            options={ATTENDANCE_STATUS_OPTIONS}
                            value={ATTENDANCE_STATUS_OPTIONS.find(
                              (option) =>
                                option.value === participant.attendanceStatus,
                            )}
                            onChange={(option) =>
                              handleParticipantChange(
                                index,
                                "attendanceStatus",
                                option?.value || "",
                              )
                            }
                          />
                        </div>

                        <div className="hr-records-card-field">
                          <label>Result</label>
                          <Select
                            className="hr-records-select"
                            options={RESULT_STATUS_OPTIONS}
                            value={RESULT_STATUS_OPTIONS.find(
                              (option) => option.value === participant.resultStatus,
                            )}
                            onChange={(option) =>
                              handleParticipantChange(
                                index,
                                "resultStatus",
                                option?.value || "",
                              )
                            }
                          />
                        </div>

                        <div className="hr-records-card-field-input">
                          <label>Score</label>
                          <input
                            type="number"
                            value={participant.score}
                            onChange={(event) =>
                              handleParticipantChange(
                                index,
                                "score",
                                event.target.value,
                              )
                            }
                            placeholder="Enter score"
                          />
                        </div>
                      </div>

                      <div className="hr-records-card-grid hr-records-card-grid-four">
                        <div className="hr-records-card-field-input">
                          <label>Certificate Number</label>
                          <input
                            type="text"
                            value={participant.certificateNumber}
                            onChange={(event) =>
                              handleParticipantChange(
                                index,
                                "certificateNumber",
                                event.target.value,
                              )
                            }
                            placeholder="Enter certificate number"
                          />
                        </div>

                        <div className="hr-records-card-field-input">
                          <label>Issue Date</label>
                          <DatePicker
                            selected={participant.issueDate}
                            onChange={(date) =>
                              handleParticipantChange(index, "issueDate", date)
                            }
                            dateFormat="dd/MM/yyyy"
                            placeholderText="Select issue date"
                            className="datesss"
                            wrapperClassName="hr-records-date-wrapper"
                            showYearDropdown
                            scrollableYearDropdown
                            yearDropdownItemNumber={30}
                          />
                        </div>

                        <div className="hr-records-card-field-input">
                          <label>Expiry Date</label>
                          <DatePicker
                            selected={participant.expiryDate}
                            onChange={(date) =>
                              handleParticipantChange(index, "expiryDate", date)
                            }
                            dateFormat="dd/MM/yyyy"
                            placeholderText="Select expiry date"
                            className="datesss"
                            wrapperClassName="hr-records-date-wrapper"
                            showYearDropdown
                            scrollableYearDropdown
                            yearDropdownItemNumber={30}
                          />
                        </div>

                        <div className="hr-records-card-field-input">
                          <label>Next Due Date</label>
                          <DatePicker
                            selected={participant.nextDueDate}
                            onChange={(date) =>
                              handleParticipantChange(index, "nextDueDate", date)
                            }
                            dateFormat="dd/MM/yyyy"
                            placeholderText="Select due date"
                            className="datesss"
                            wrapperClassName="hr-records-date-wrapper"
                            showYearDropdown
                            scrollableYearDropdown
                            yearDropdownItemNumber={30}
                          />
                        </div>
                      </div>

                      <div className="hr-records-card-grid hr-records-card-grid-three">
                        <div className="hr-records-card-field-input hr-records-card-field-span-2">
                          <label>Remarks</label>
                          <textarea
                            rows={2}
                            value={participant.remarks}
                            onChange={(event) =>
                              handleParticipantChange(
                                index,
                                "remarks",
                                event.target.value,
                              )
                            }
                            style={{ width: "100%", resize: "vertical" }}
                            placeholder="Participant remarks"
                          />
                        </div>
                        <div className="hr-records-card-upload">
                          <label>Attachments</label>
                          <HrUploadControl
                            buttonText="Upload File"
                            popupTitle="Certificate Attachments"
                            newFiles={participant.newCertificateFiles}
                            existingFiles={
                              existingParticipantAttachments[
                                participant.participantId
                              ] || []
                            }
                            onFilesSelected={(files) =>
                              addParticipantCertificates(index, files)
                            }
                            onValidationError={(message) =>
                              showSnackbar(message, "error")
                            }
                            onRemoveNewFile={(fileIndex) =>
                              removeParticipantCertificate(index, fileIndex)
                            }
                            onRemoveExistingFile={(file) =>
                              removeExistingParticipantCertificate(
                                participant.participantId,
                                file.file_id || file.id,
                              )
                            }
                            onViewExistingFile={(file) =>
                              handleFileAction(
                                hrRecordsApi.viewParticipantAttachment(
                                  file.file_id || file.id,
                                ),
                                file.filename || file.name,
                                "view",
                              )
                            }
                            onDownloadExistingFile={(file) =>
                              handleFileAction(
                                hrRecordsApi.downloadParticipantAttachment(
                                  file.file_id || file.id,
                                ),
                                file.filename || file.name,
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="hr-records-add-card-button"
                    onClick={addParticipant}
                  >
                    <HiOutlinePlus /> Add Participant
                  </button>
                </div>

                <div className="hr-records-section">
                  <div className="hr-records-section-title">Session Attachments</div>
                  <div className="hr-records-session-documents">
                  {[
                    ["attendanceSheets", "Attendance Sheets"],
                    ["photographs", "Photographs"],
                    ["materials", "Materials"],
                    ["attachments", "Other Attachments"],
                  ].map(([bucket, label]) => {
                    const existingKey =
                      bucket === "attendanceSheets"
                        ? "attendance_sheet"
                        : bucket === "photographs"
                          ? "photograph"
                          : bucket === "materials"
                            ? "material"
                            : "other";

                    return (
                      <div key={bucket} className="hr-records-session-document-item">
                        <div className="hr-records-upload-row">
                          <strong>{label}</strong>
                          <HrUploadControl
                            buttonText="Upload File"
                            popupTitle={`${label} Attachments`}
                            newFiles={sessionFiles[bucket]}
                            existingFiles={existingSessionAttachments[existingKey] || []}
                            accept={
                              bucket === "photographs"
                                ? HR_IMAGE_UPLOAD_ACCEPT
                                : undefined
                            }
                            allowedExtensions={
                              bucket === "photographs"
                                ? HR_IMAGE_UPLOAD_EXTENSIONS
                                : undefined
                            }
                            onFilesSelected={(files) => addFilesToBucket(bucket, files)}
                            onValidationError={(message) =>
                              showSnackbar(message, "error")
                            }
                            onRemoveNewFile={(fileIndex) =>
                              removeNewSessionFile(bucket, fileIndex)
                            }
                            onRemoveExistingFile={(file) =>
                              removeExistingSessionFile(file.file_id || file.id)
                            }
                            onViewExistingFile={(file) =>
                              handleFileAction(
                                hrRecordsApi.viewTrainingSessionAttachment(
                                  file.file_id || file.id,
                                ),
                                file.filename || file.name,
                                "view",
                              )
                            }
                            onDownloadExistingFile={(file) =>
                              handleFileAction(
                                hrRecordsApi.downloadTrainingSessionAttachment(
                                  file.file_id || file.id,
                                ),
                                file.filename || file.name,
                              )
                            }
                          />
                        </div>
                      </div>
                    );
                  })}
                  </div>
                </div>
              </form>
            )}
          </div>

          <div className="footer">
            <div className="button-group">
              <button
                className="data-form-save-button"
                type="button"
                onClick={handleSave}
                disabled={loading || pageLoading}
              >
                {loading ? "Saving..." : "Save"}
              </button>
              <button
                className="data-form-cancel-button"
                type="button"
                onClick={() => navigate("/training/training-sessions")}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert severity={snackbarSeverity}>{snackbarMessage}</MuiAlert>
      </Snackbar>
    </>
  );
}

export default TrainingSessionFormPage;
